import {onBeforeUnmount, onMounted, ref, Ref} from 'vue';
import {useTranslation} from '@malt/vue3-use';
import {getPageCountryCode, getPageLanguageCode} from '@malt/dom';
import {defaultCitiesSuggestions, LocationAutocomplete} from '@malt/location-autocomplete';
import {useRemoteLabels} from './useRemoteLabels';
export interface Point {
    lat: number;
    lon: number;
}

export interface Address {
    administrativeAreaLevel1?: string;
    administrativeAreaLevel1Code?: string;
    administrativeAreaLevel2?: string;
    administrativeAreaLevel2Code?: string;
    administrativeAreaLevel3?: string;
    administrativeAreaLevel3Code?: string;
    administrativeAreaLevel4?: string;
    administrativeAreaLevel4Code?: string;
    city?: string;
    country?: string;
    countryCode?: string;
    formattedAddress?: string;
    loc?: Point;
    region?: string;
    remoteAllowed?: boolean;
    remoteEuropa?: boolean;
    street?: string;
    street2?: string;
    zipCode?: string;
}

type OnSelectFunction = (selectedAddress: Address) => void;

export function useLocationAutocomplete(searchParams: Record<string, string>, input: Ref<HTMLInputElement | null>, onSelect: OnSelectFunction) {
    let locationAutocomplete: any = null;

    const {remoteCountryLabel} = useRemoteLabels();

    const defaultAddress = ref<Address>({
        formattedAddress: searchParams.location ?? remoteCountryLabel,
        remoteAllowed: !searchParams.location || !!searchParams.remoteAllowed,
        remoteEuropa: !!searchParams.remoteEuropa,
    });

    const {msg} = useTranslation();

    onMounted(() => {
        const languageCode = getPageLanguageCode();
        const countryCode = getPageCountryCode();

        const suggestions = defaultCitiesSuggestions[`${languageCode}_${countryCode}`] || defaultCitiesSuggestions[countryCode] || [];
        const focusPoint = window.GEOLOCATION_FOCUS_POINT || (suggestions && suggestions.length > 0 ? suggestions[0].loc : null);

        locationAutocomplete = new LocationAutocomplete({
            allowRemote: true,
            allowRemoteEuropa: window.features.allowRemoteEuropa,
            countryCode,
            defaultCitySuggestions: suggestions,
            defaultValue: defaultAddress.value,
            displaySuggestions: true,
            focusPoint,
            input: input.value,
            languageCode,
            layer: 'LOCALITY',
            remoteIfEmpty: true,
            remoteCountryOptionLabel: remoteCountryLabel,
            remoteEuropaLabel: window.features.allowRemoteEuropa ? msg(`search.location.remote.europa`) : null,
            onSelect,
        });
    });

    onBeforeUnmount(() => {
        locationAutocomplete.destroy();
    });
}
